/*
  Kingspan - Expivi Step Bar (blue navigation bar)

  A horizontal bar of step tabs (Choose region, Tank type, Dimensions, Colour,
  Inlet/Overflow/Outlet position(s), Choose addons). Clicking a tab activates +
  scrolls to the matching step in the Expivi options panel (the 3D camera reacts
  on its own, natively, via the Expivi catalogue rules — no camera code here).

  The Expivi configurator runs inside the srcdoc iframe (#expivi-configurator-iframe).
  Because srcdoc is same-origin, we inject the bar + handlers INTO the iframe document,
  so the bar and the step headers (.step-header-name) live in the same document.

  GATED: only runs on the full-page builder. Activates when the page contains
  #expivi-3d-modal[data-stepbar="1"] (set by snippets/expivi-3d-page.liquid).
  It never touches the normal PDP "Build in 3D" modal.
*/
(function () {
  "use strict";

  var modal = document.getElementById("expivi-3d-modal");
  if (!modal || modal.getAttribute("data-stepbar") !== "1") return;

  var iframe = document.getElementById("expivi-configurator-iframe");
  if (!iframe) return;

  var BAR_ID = "ksp-stepbar";
  var poll = null;

  function styleText() {
    return [
      "#" + BAR_ID + "{position:fixed;top:0;left:0;right:0;z-index:2000;display:flex;align-items:stretch;",
      "  width:100%;box-sizing:border-box;",
      "  background:#0a1f44;overflow-x:auto;scrollbar-width:none;-ms-overflow-style:none;",
      "  font-family:'Kumbh Sans',Helvetica,Arial,sans-serif;cursor:grab;}",
      "#" + BAR_ID + "::-webkit-scrollbar{display:none;}",
      "#" + BAR_ID + ".dragging{cursor:grabbing;}",
      "#" + BAR_ID + " .ksp-tab{flex:0 0 auto;padding:16px 22px;color:#fff;font-size:15px;",
      "  font-weight:500;white-space:nowrap;cursor:pointer;border:none;background:transparent;",
      "  user-select:none;transition:background .15s ease;}",
      "#" + BAR_ID + " .ksp-tab:hover{background:rgba(255,255,255,.08);}",
      "#" + BAR_ID + " .ksp-tab:focus,#" + BAR_ID + " .ksp-tab:focus-visible{outline:none;box-shadow:none;}",
      // step headers clear the fixed bar when scrolled into view (no hidden-under-bar)
      ".step-header-name{scroll-margin-top:64px;}"
    ].join("\n");
  }

  function buildBar(doc, win) {
    if (doc.getElementById(BAR_ID)) return true;

    var headers = doc.querySelectorAll(".step-header-name");
    if (!headers.length) return false;

    // inject styles
    if (!doc.getElementById(BAR_ID + "-styles")) {
      var st = doc.createElement("style");
      st.id = BAR_ID + "-styles";
      st.textContent = styleText();
      doc.head.appendChild(st);
    }

    var bar = doc.createElement("nav");
    bar.id = BAR_ID;
    bar.style.width = "100%";
    bar.style.boxSizing = "border-box";

    headers.forEach(function (h) {
      var label = (h.textContent || "").trim();
      if (!label) return;
      var tab = doc.createElement("button");
      tab.type = "button";
      tab.className = "ksp-tab";
      tab.textContent = label;
      tab.addEventListener("click", function () {
        goToStep(doc, label, tab, bar);
      });
      bar.appendChild(tab);
    });

    // Final "Summary" tab — opens the Configuration overview modal (preview only,
    // no add-to-cart). The implementation lives in expivi-modal.js and is exposed
    // as window.KingspanExpivi.previewSummary.
    var summaryTab = doc.createElement("button");
    summaryTab.type = "button";
    summaryTab.className = "ksp-tab ksp-tab--summary";
    summaryTab.textContent = "Summary";
    summaryTab.addEventListener("click", function () {
      var api = window.KingspanExpivi;
      if (api && typeof api.previewSummary === "function") {
        api.previewSummary();
      }
    });
    bar.appendChild(summaryTab);

    // place the bar as a full-width horizontal strip ABOVE the viewer+options row
    var host =
      doc.querySelector(".expivi-page-container") ||
      doc.querySelector(".configurator-container") ||
      doc.body;
    if (host === doc.body || !host.parentNode) {
      doc.body.insertBefore(bar, doc.body.firstChild);
    } else {
      host.parentNode.insertBefore(bar, host);
    }

    enableDragScroll(bar, win);

    // The Expivi configurator container is position:absolute; top:0, so it would
    // sit UNDER the fixed bar. Push it down by the bar height so both are visible.
    // Also cap its height to (iframe viewport − stepbar) so the iframe never gets
    // an internal scrollbar — option-column scrolls internally instead.
    //
    // Important: Expivi's own CSS forces `bottom: -51px` + `min-height: 100%` on
    // .expivi-page-container, which OVERRIDES our inline `height` (absolute
    // positioning with both top + bottom set computes height from them, ignoring
    // the height property). Use setProperty with 'important' priority to win.
    var pageContainer = doc.querySelector(".expivi-page-container");
    var availableH = Math.max(0, win.innerHeight - bar.offsetHeight);
    if (pageContainer) {
      pageContainer.style.setProperty("top", bar.offsetHeight + "px", "important");
      pageContainer.style.setProperty("bottom", "auto", "important");
      pageContainer.style.setProperty("min-height", "0", "important");
      pageContainer.style.setProperty("height", availableH + "px", "important");
      pageContainer.style.setProperty("max-height", availableH + "px", "important");
      pageContainer.style.setProperty("overflow", "hidden", "important");
    }
    var optionCol = doc.querySelector(".option-container");
    if (optionCol) {
      optionCol.style.setProperty("height", "100%", "important");
      optionCol.style.setProperty("max-height", "100%", "important");
      optionCol.style.setProperty("overflow-y", "auto", "important");
    }
    // Also block the document itself from scrolling (in case anything escapes
    // the container constraints).
    doc.documentElement.style.overflow = "hidden";
    doc.body.style.overflow = "hidden";

    // Mark stepbar mode + expose bar height so CSS can also target this mode
    // (used by other rules to avoid affecting the PDP modal).
    doc.body.classList.add("ksp-stepbar-mode");
    doc.documentElement.style.setProperty("--ksp-stepbar-h", bar.offsetHeight + "px");

    // Viewer sizing + mobile scroll.
    //   Desktop: viewer fills the height (side-by-side columns; the natural canvas
    //   height ~829px would otherwise push the bottom-right toolbar below the view).
    //   Mobile (<=600px): the layout stacks (viewer on top, options below). The
    //   viewer only needs ~40vh; the options then scroll in the .configurator-container
    //   (which does NOT contain the viewer, so the fixed viewer never goes black on
    //   scroll). Two gotchas handled below:
    //     1) Expivi re-creates/re-sizes the viewer during init, so a one-shot write
    //        binds to a stale element — re-apply on a short interval.
    //     2) Changing the viewer height leaves the WebGL canvas blank until a resize
    //        re-render, so dispatch a 'resize' whenever we actually change the height.
    //   All writes are single-element — no body/subtree observers.
    function isMobileNow() { return win.matchMedia("(max-width:600px)").matches; }
    function viewerTargetH() {
      return isMobileNow() ? Math.round(win.innerHeight * 0.4)
                           : Math.max(0, win.innerHeight - bar.offsetHeight);
    }
    function applyLayout() {
      var viewerCol = doc.querySelector(".viewer-container, #expivi-viewer-container");
      var changed = false;
      if (viewerCol) {
        var vh = viewerTargetH() + "px";
        if (viewerCol.style.height !== vh || viewerCol.style.maxHeight !== vh) {
          viewerCol.style.setProperty("height", vh, "important");
          viewerCol.style.setProperty("max-height", vh, "important");
          changed = true;
        }
      }
      if (isMobileNow()) {
        var conf = doc.querySelector(".configurator-container");
        if (conf) {
          conf.style.setProperty("max-height", Math.max(0, win.innerHeight - bar.offsetHeight) + "px", "important");
          conf.style.setProperty("overflow-y", "auto", "important");
          conf.style.setProperty("overflow-x", "hidden", "important");
        }
      }
      // Re-render the 3D canvas at the new size (it goes blank otherwise).
      if (changed) win.setTimeout(function () { win.dispatchEvent(new win.Event("resize")); }, 0);
    }
    applyLayout();
    var layoutTicks = 0;
    var layoutIv = win.setInterval(function () {
      applyLayout();
      if (++layoutTicks > 60) win.clearInterval(layoutIv);
    }, 400);

    // Re-fit on real window resize (parent viewport / orientation changes).
    win.addEventListener("resize", function () {
      var h = Math.max(0, win.innerHeight - bar.offsetHeight);
      if (pageContainer) {
        pageContainer.style.setProperty("height", h + "px", "important");
        pageContainer.style.setProperty("max-height", h + "px", "important");
      }
      applyLayout();
    });

    return true;
  }

  // Click + scroll to the matching step: set the tab active, then click the step
  // header so the Expivi stepper opens that step (= "click step N") and scroll to it.
  function goToStep(doc, label, tab, bar) {
    // No "active"/selected highlight on the tabs (matches the reference behaviour).
    var header = Array.prototype.slice
      .call(doc.querySelectorAll(".step-header-name"))
      .find(function (p) { return (p.textContent || "").trim() === label; });
    if (!header) return;

    // click the step row so Expivi activates/opens that step (the 3D reacts to this)
    var stepRow = header.closest(".v-stepper__step") || header.closest(".step-header-container");
    if (stepRow) {
      try { stepRow.click(); } catch (e) {}
    }

    // Wait for the stepper expand/collapse (accordion) to settle, THEN scroll —
    // otherwise the layout shifts after we compute the target and the scroll overshoots.
    // Scroll ONLY inside the iframe (don't bubble to the outer page), leaving room
    // for the fixed bar on top.
    setTimeout(function () {
      var se = doc.scrollingElement || doc.documentElement;
      var theBar = doc.getElementById(BAR_ID);
      var offset = (theBar ? theBar.offsetHeight : 51) + 12;
      var target = header.getBoundingClientRect().top + se.scrollTop - offset;
      se.scrollTo({ top: target < 0 ? 0 : target, behavior: "smooth" });
    }, 380);
  }

  // simple drag-to-scroll for the horizontal bar (desktop + touch)
  function enableDragScroll(bar, win) {
    var down = false, startX = 0, startScroll = 0, moved = false;
    bar.addEventListener("mousedown", function (e) {
      down = true; moved = false; startX = e.pageX; startScroll = bar.scrollLeft; bar.classList.add("dragging");
    });
    win.addEventListener("mousemove", function (e) {
      if (!down) return;
      var dx = e.pageX - startX;
      if (Math.abs(dx) > 3) moved = true;
      bar.scrollLeft = startScroll - dx;
    });
    win.addEventListener("mouseup", function () { down = false; bar.classList.remove("dragging"); });
    // prevent a drag from also firing a tab click
    bar.addEventListener("click", function (e) { if (moved) { e.stopPropagation(); e.preventDefault(); } }, true);
  }

  function tryInject() {
    var doc, win;
    try { doc = iframe.contentDocument; win = iframe.contentWindow; } catch (e) { return; }
    if (!doc || !win) return;
    if (buildBar(doc, win)) {
      if (poll) { clearInterval(poll); poll = null; }
    }
  }

  // The iframe srcdoc is (re)written by expivi-modal.js when the builder opens.
  // Poll until the Expivi options render the step headers, then inject once.
  function startPolling() {
    if (poll) clearInterval(poll);
    var attempts = 0;
    poll = setInterval(function () {
      attempts++;
      tryInject();
      if (attempts > 120 && poll) { clearInterval(poll); poll = null; } // ~60s safety
    }, 500);
  }

  // restart polling whenever the iframe reloads (each time the builder opens)
  iframe.addEventListener("load", startPolling);
  startPolling();
})();
